import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * An instant pop-up on hover (Duncan 2026-10-28: "the instant pop-up ... like we do if you scroll over the gantt chart
 * bar"): the Schedule Gantt bar's dark card (GanttTab's tooltip) for anything else. The browser's own `title` waits a
 * second and can't be styled.
 *
 * One per list: `const [tip, tipOn] = useHoverTip();`, then `{...tipOn(content)}` on each thing and `{tip}` once.
 * `content` is a node, or text whose first line is its title. It shows on keyboard focus too, stays on screen at the
 * edges, and goes on a click or a scroll. Portaled into .app, where the dark-mode tokens live (body only has the light
 * ones), above a Lift.
 */
export default function useHoverTip({ maxWidth = 280 } = {}) {
  const [shown, setShown] = useState(null); // { x, y, content }
  const ref = useRef(null);

  // Right of and just above the pointer, as the Gantt's; flipped left / lifted where it would run off the screen.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !shown) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    let left = shown.x + 12;
    let top = shown.y - 10;
    if (left + r.width > window.innerWidth - pad) left = Math.max(pad, shown.x - 12 - r.width);
    if (top + r.height > window.innerHeight - pad) top = Math.max(pad, window.innerHeight - pad - r.height);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = 'visible';
  }, [shown]);

  useEffect(() => {
    if (!shown) return undefined;
    const hide = () => setShown(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [shown]);

  const tipOn = useCallback((content) => ({
    onMouseEnter: (e) => setShown({ x: e.clientX, y: e.clientY, content }),
    onMouseLeave: () => setShown(null),
    onMouseDown: () => setShown(null),
    onFocus: (e) => {
      // From the keyboard only: a click focuses a button too, and its pop-up would stay up over what the click opened.
      let keyboard = true;
      try { keyboard = e.currentTarget.matches(':focus-visible'); } catch (err) { /* an older browser: show it */ }
      if (!keyboard) return;
      const r = e.currentTarget.getBoundingClientRect();
      setShown({ x: r.left - 12, y: r.bottom + 16, content });
    },
    onBlur: () => setShown(null),
  }), []);

  const tip = shown && shown.content ? createPortal(
    <div ref={ref} role="tooltip" style={{
      position: 'fixed', left: 0, top: 0, visibility: 'hidden', zIndex: 10000, pointerEvents: 'none',
      backgroundColor: 'var(--text-1)', color: 'var(--bg-surface)', padding: '10px 14px', borderRadius: 8,
      fontSize: 13.5, lineHeight: 1.5, maxWidth, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', textAlign: 'left',
    }}>
      {typeof shown.content === 'string'
        ? shown.content.split('\n').map((line, i) => (
          <div key={i} style={i ? undefined : { fontWeight: 600, marginBottom: 4 }}>{line}</div>
        ))
        : shown.content}
    </div>,
    document.querySelector('.app') || document.body,
  ) : null;

  return [tip, tipOn];
}
